import { doc, DocumentData, setDoc, Timestamp } from 'firebase/firestore'
import * as React from 'react'
import { Data } from 'react-firebase-hooks/firestore/dist/firestore/types'
import { firebaseAuth, firestore } from '../../config/firebase'
import { Button } from '../../shared-components/Button'
import { useScreenTracking } from '../../shared-hooks/useScreenTracking'
import { USERS_COLLECTION } from '../../shared-hooks/useUserData'
import { SESSIONS_SUBCOLLECTION } from '../../shared-hooks/useUserSessions'
import {
  DAY_MS,
  HOUR_MS,
  NUM_DAYS_TO_SHOW,
  TOMORROW_MS,
} from './session-picker/SessionPicker'
import { SignupCard } from './SignupCard'
import { SignupStep } from './SignupFlow'

const TODAY_MS = new Date().setHours(0, 0, 0, 0)

type AlwaysOnSummaryProps = {
  userData: Data<DocumentData, '', ''>
  setTotalGuarantee: React.Dispatch<React.SetStateAction<number>>
  totalGuarantee: number
  setSignupStep: (step: SignupStep) => void
}

export const AlwaysOnSummary = ({
  userData,
  setTotalGuarantee,
  totalGuarantee,
  setSignupStep,
}: AlwaysOnSummaryProps): JSX.Element => {
  useScreenTracking('AlwaysOnSummaryView', 'View always on summary')

  const [isSaving, setIsSaving] = React.useState(false)

  React.useEffect(() => {
    if (!userData) {
      return
    }

    const {
      rates: {
        base_rate: { rate },
        day_multipliers,
        time_block_multipliers,
      },
    } = userData

    const maxDayMultiplier = Math.max(...Object.values(day_multipliers))
    const maxTimeBlockMultiplier = Math.max(
      ...Object.values(time_block_multipliers)
    )
    const maxRate = maxDayMultiplier * maxTimeBlockMultiplier * rate
    const guarantee = (30 * rate + 10 * maxRate) / 100
    setTotalGuarantee(guarantee)
  })

  const handleClick = async () => {
    setIsSaving(true)

    if (!userData) {
      setIsSaving(false)
      return
    }

    // Update user doc.
    const endDate = new Date(TOMORROW_MS + (NUM_DAYS_TO_SHOW - 1) * DAY_MS)
    const userDocRef = doc(
      firestore,
      USERS_COLLECTION,
      firebaseAuth.currentUser.uid
    )
    const userUpdate = setDoc(
      userDocRef,
      {
        trial_end_date: Timestamp.fromDate(endDate),
        is_always_on: true,
      },
      { merge: true }
    )

    const firestoreWrites: Promise<void>[] = [userUpdate]

    // Write sessions for the next week.
    const currTimeBlockStartHour = Math.floor(new Date().getHours() / 6) * 6
    let currStartTime = new Date(TODAY_MS + currTimeBlockStartHour * HOUR_MS)
    for (let i = 0; i < 4 * 7; i++) {
      currStartTime = new Date(currStartTime.getTime() + 6 * HOUR_MS)

      const endTime = new Date(currStartTime.getTime() + 6 * HOUR_MS)
      const dayOfWeek = (currStartTime.getDay() + 6) % 7
      const timeOfDay = Math.floor(currStartTime.getHours() / 6)

      const sessionData = {
        start_time: Timestamp.fromDate(currStartTime),
        end_time: Timestamp.fromDate(endTime),
        duration_hours: 6,
        time_of_day: timeOfDay,
        day_of_week: dayOfWeek,
        shift_online_time: 0,
        paid: 0,
        points: 0,
        earned: 0,
        timestamp: Timestamp.fromMillis(Date.now()),
      }

      const sessionDocRef = doc(
        firestore,
        USERS_COLLECTION,
        firebaseAuth.currentUser.uid,
        SESSIONS_SUBCOLLECTION,
        currStartTime.getTime().toString()
      )
      firestoreWrites.push(setDoc(sessionDocRef, sessionData, { merge: true }))
    }

    await Promise.allSettled(firestoreWrites)

    setIsSaving(false)
    setSignupStep(SignupStep.Plaid)
  }

  return (
    <SignupCard
      heading=''
      headerComponent={
        <h1 className='text-2xl max-w-fit leading-snug mb-4'>
          Based on your driving history, you could be guaranteed up to
        </h1>
      }
    >
      <div className='mt-4 flex'>
        <p className='inline text-6xl font-semibold text-brand-light-purple flex-none'>
          ${totalGuarantee.toFixed(2)}
        </p>
        <p className='ml-4 mt-2 text-sm font-extralight inline'>
          +tips &amp; bonuses
        </p>
      </div>
      <p className='ml-1 text-sm font-extralight leading-6 text-body-black mt-11'>
        † This total is based on 40 hours of driving.
      </p>
      <p className='ml-1 text-sm font-extralight leading-6 text-body-black mt-2.5'>
        † Your guarantee will change based on how much and what times you
        actually drive.
      </p>
      <div className='mt-10'>
        <Button type='button' isSubmitting={isSaving} onClick={handleClick}>
          Start my free trial
        </Button>
      </div>
    </SignupCard>
  )
}
